import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import { AppShell } from "../components/app-shell.js";
import { useConfirm } from "../features/canvas-kit/ui/confirm-dialog.jsx";
import { WhiteboardEditor } from "../features/whiteboard/editor.jsx";
import {
  parseWhiteboardSnapshot,
  type WhiteboardSnapshot,
} from "../features/whiteboard/snapshot.js";
import { useSync } from "../providers/sync-provider";
import type { BoardRow } from "../repos/boards.js";
import { listPlayers, type PlayerRow } from "../repos/players.js";
import { deleteWhiteboard, listWhiteboards, updateWhiteboard } from "../repos/whiteboards.js";

/**
 * Whiteboard editor route — `/whiteboard/:boardId`.
 *
 * Looks up a single whiteboard row from the local SQLite cache and mounts
 * the Konva editor. Missing/unknown ids redirect back to the list so deep
 * links degrade gracefully.
 */
export function WhiteboardEditorPage() {
  const { boardId } = useParams<{ boardId: string }>();
  const navigate = useNavigate();
  const { db, orgId, userId, engine, defaultTeamId } = useSync();
  const qc = useQueryClient();
  const ready = Boolean(db && orgId && userId);
  const boards = useQuery<BoardRow[]>({
    queryKey: ["whiteboards", orgId, userId],
    enabled: ready,
    queryFn: () => listWhiteboards(db!, orgId!, userId!),
    refetchInterval: 5000,
  });

  const playersQuery = useQuery<PlayerRow[]>({
    queryKey: ["whiteboard", "players", orgId, defaultTeamId],
    enabled: Boolean(db && orgId && defaultTeamId),
    queryFn: () => listPlayers(db!, orgId!, defaultTeamId!),
    refetchInterval: 15000,
  });

  const active = useMemo(
    () => boards.data?.find((b) => b.id === boardId) ?? null,
    [boards.data, boardId],
  );

  useEffect(() => {
    if (!ready) return;
    if (boards.isLoading) return;
    if (!active && boardId) navigate("/whiteboard", { replace: true });
  }, [ready, boards.isLoading, active, boardId, navigate]);

  const save = useMutation({
    mutationFn: async (args: { id: string; name: string; snapshot: WhiteboardSnapshot }) => {
      if (!ready) throw new Error("Lokale Datenbank nicht bereit.");
      await updateWhiteboard(db!, {
        id: args.id,
        name: args.name,
        snapshot: args.snapshot,
      });
      void engine?.syncNow();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["whiteboards"], exact: false });
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      if (!ready) throw new Error("Lokale Datenbank nicht bereit.");
      await deleteWhiteboard(db!, { id, orgId: orgId! });
      void engine?.syncNow();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["whiteboards"], exact: false });
      navigate("/whiteboard", { replace: true });
    },
  });

  const snap = useMemo(
    () => (active ? parseWhiteboardSnapshot(active.snapshot_json) : null),
    // Re-parse only when the underlying JSON string actually changes, so
    // periodic refetches that return identical content don't produce a new
    // snapshot object identity (which would otherwise wipe unsaved edits in
    // the editor's `useEffect([snapshot])`).
    [active?.snapshot_json],
  );

  const { confirm, dialog } = useConfirm();

  if (!active || !snap) {
    return (
      <AppShell title="Whiteboard" wide chromeless>
        <div className="flex h-[60vh] items-center justify-center text-body-sm text-muted-foreground">
          {boards.isLoading ? "Lade Whiteboard…" : "Whiteboard nicht gefunden."}
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell title="Whiteboard" wide chromeless>
      <WhiteboardEditor
        key={active.id}
        name={active.name}
        snapshot={snap}
        saving={save.isPending}
        players={playersQuery.data ?? []}
        onClose={() => navigate("/whiteboard")}
        onSave={({ name, snapshot }) => save.mutate({ id: active.id, name, snapshot })}
        onDelete={async () => {
          const ok = await confirm({
            title: "Whiteboard löschen?",
            message: `„${active.name}“ wird unwiderruflich gelöscht.`,
            confirmLabel: "Löschen",
          });
          if (ok) remove.mutate(active.id);
        }}
      />
      {dialog}
    </AppShell>
  );
}
